import { supabase } from '@/integrations/supabase/client';
import { ContactInfo } from '@/types/assessment';
import { LocalAssessmentData } from '@/utils/assessmentLocalStorage';
import { transformScoresForDatabase } from '@/utils/assessmentValidation';
import type { Database } from '@/integrations/supabase/types';

// Temporary helper to bypass strict RPC name typing from generated Supabase types.
// This keeps our code compiling even if the generated types don't yet include new RPCs.
const callRpc = async <T = unknown>(fn: string, params: Record<string, any>) => {
  return (supabase as any).rpc(fn, params) as Promise<{ data: T | null; error: any | null }>;
};

// Upsert contact via secured RPC (no direct table write)
export const upsertContact = async (contactInfo: ContactInfo, assessmentType: string): Promise<string> => {
  const normalizedEmail = (contactInfo.email || '').trim().toLowerCase();
  const firstName = (contactInfo.firstName || '').trim();
  const lastName = (contactInfo.lastName || '').trim();
  const company = (contactInfo.company || '').trim();
  const userFunction = (contactInfo.function || '').trim();
  const phone = (contactInfo.phone || '').trim();
  const sector = (contactInfo.sector || '').trim();

  if (!normalizedEmail || !firstName || !lastName || !company) {
    throw new Error('Invalid contact data: missing required fields');
  }

  const rpcPayload = {
    p_email: normalizedEmail,
    p_first_name: firstName,
    p_last_name: lastName,
    p_phone: phone || '',
    p_company: company,
    p_function: userFunction || '',
    // Legacy parameter kept only for the existing RPC signature.
    // The database no longer treats it as consent or requires it to be true.
    p_rgpd_consent: false,
    p_source: `assessment-${assessmentType}`,
    p_sector: sector || '',
    // Marketing consent is managed by HubSpot's native consent mechanism.
    p_marketing_consent: false,
  };

  const { data, error } = await callRpc<string>('secure_upsert_contact', rpcPayload);

  if (error) throw error;
  if (!data) throw new Error('Failed to get contact ID after RPC');

  return String(data);
};

// Upsert assessment via secured RPC (no direct table write)
export const upsertAssessment = async (contactId: string, localData: LocalAssessmentData): Promise<string> => {
  if (!contactId || contactId === 'undefined') {
    throw new Error(`Invalid contactId: ${contactId}`);
  }

  const transformedScores = transformScoresForDatabase(localData.scores);

  const rpcPayload = {
    p_contact_id: contactId,
    p_assessment_type: localData.assessmentType,
    p_scores: transformedScores as unknown as Database['public']['Tables']['assessment_submissions']['Row']['scores'],
    p_answers: localData.answers as Database['public']['Tables']['assessment_submissions']['Row']['answers'],
    p_overall_score: localData.overallScore ?? null,
    p_detailed_answers: (localData.detailedAnswers || localData.answers) as Database['public']['Tables']['assessment_submissions']['Row']['detailed_answers'],
    p_questions_metadata: (localData.questionsMetadata || {}) as Database['public']['Tables']['assessment_submissions']['Row']['questions_metadata'],
    p_ai_advice: null as unknown as Database['public']['Tables']['assessment_submissions']['Row']['ai_advice'],
  };

  const { data, error } = await callRpc<string>('secure_upsert_assessment', rpcPayload);

  if (error) throw error;
  if (!data) throw new Error('Failed to get submission ID after RPC');

  return String(data);
};

// Maintenir la compatibilité avec l'ancien nom de fonction
export const insertAssessment = upsertAssessment;
